import type { CompletenessCheck, ProjectionWarning, TargetSchema } from "@/types";
import type { TransformationRowDecisionsPayload } from "@/types/documentTransformation";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { describeAdvisory } from "./advisoryCopy";
import type { YtdReconciliation } from "./ytdReconciliation";
import type { CriticalFieldAdvisoryEntry } from "../hooks/useCriticalFieldAdvisory";
import type { PaidDateCoverage } from "./paidDateCoverage";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

/**
 * The "Before you approve" rail's one list of checks (#7432).
 *
 * Every pre-approval signal the review surface has — field-mapping issues,
 * rows that cannot load, the reported-YTD reconciliation, the paid-through
 * date, projection advisories, completeness, the Rx double-count warning —
 * becomes either a CARD (needs attention: blocking in error tone, advisory in
 * warning tone, each with its own action) or a PASS LINE (nothing to do). The
 * attention count is the number of cards, and it is what disables Approve.
 *
 * Advisory cards can be acknowledged ("Differences are expected"): the card
 * becomes a pass line with Undo, stops counting, and the acknowledgement
 * travels with the approval (#7440 AC6). Blocking cards cannot — the only way
 * past "3 claim lines can't load" is to fix the mapping or exclude the rows.
 *
 * Pure: inputs in, cards and lines out, with the click handlers already bound,
 * so the whole catalogue is testable without rendering.
 */
export type ReviewCheckSeverity = "blocking" | "advisory";

export interface ReviewCheckAction {
  label: string;
  onClick: () => void;
  /** Outlined for the primary fix ("Open field mapping"); text for navigation ("Show the 3"). */
  variant?: "outlined" | "text";
  ariaLabel?: string;
  /**
   * In-app route when the action leaves the page ("Open Claim Items", #7380):
   * the rail renders a real link so it can open in a new tab; `onClick` is then
   * a no-op kept for callers that drive actions without rendering.
   */
  href?: string;
}

/** A row inside a card, e.g. one errored-records reason: "Service date unreadable · 1 · Show in grid · Exclude 1". */
export interface ReviewCheckRow {
  key: string;
  label: string;
  count: number;
  /** Trailing figure after the label: "$12,340.00" (#7436). */
  detail?: string;
  /**
   * What Exclude acts on when fewer rows are loaded than `count` (a paged
   * document whose projection covered the whole file): the button says
   * "Exclude 2" while the row still counts 5.
   */
  excludeCount?: number;
  onShow?: () => void;
  onExclude?: () => void;
}

export interface ReviewCheckAcknowledge {
  /** Button label: "Differences are expected". */
  label: string;
  /** What is recorded with the approval (#7440): the line the card showed. */
  detail: string;
  /** Pass-line text once acknowledged: "3 YTD differences marked expected by you". */
  passText: string;
  undoLabel: string;
}

export interface ReviewCheckCard {
  /** Stable key — also the acknowledgement's `check`. */
  key: string;
  severity: ReviewCheckSeverity;
  title: string;
  body?: string;
  /** Tooltip behind an info icon: why this matters. */
  hint?: string;
  rows?: ReviewCheckRow[];
  actions: ReviewCheckAction[];
  /** Absent on blocking cards and when the viewer cannot act. */
  acknowledge?: ReviewCheckAcknowledge;
  /** Short name for the sticky bar's "what needs attention" summary. */
  shortName: string;
}

export interface ReviewPassLine {
  key: string;
  text: string;
  /** pass = green check; pending = still checking; note = information with an icon; plain = information, no icon. */
  state: "pass" | "pending" | "note" | "plain";
  /** Tooltip behind an info icon: why this is worth knowing. */
  hint?: string;
  action?: { label: string; onClick: () => void; ariaLabel?: string };
}

/** What travels with the approval (#7440 AC6): an acknowledged card's key and the line it showed. */
export interface PlacedAcknowledgement {
  check: string;
  detail: string;
}

export interface ReviewChecks {
  cards: ReviewCheckCard[];
  passLines: ReviewPassLine[];
  /**
   * The acknowledgements the rail actually honoured this render — one per
   * acknowledged pass line. THIS is what the approval sends, not the raw
   * browser map: an acknowledgement whose state has since changed (more
   * mismatches loaded, a date set, the rows excluded) is not in here, because
   * its card came back or disappeared (!4936 review, [AEM-ack-stale]).
   */
  acknowledgements: PlacedAcknowledgement[];
  attentionCount: number;
  attentionNames: string[];
  isChecking: boolean;
  checksDone: number;
  checksTotal: number;
}

export const CHECK_MONEY_DATE_COLUMNS = "money_date_columns";
export const CHECK_YTD_MISMATCH = "ytd_mismatch";
export const CHECK_ROWS_CANNOT_LOAD = "rows_cannot_load";
export const CHECK_PAID_THROUGH = "paid_through_date";
export const CHECK_COMPLETENESS = "completeness";
export const CHECK_RX_DOUBLE_COUNT = "rx_double_count";
/** Paid amount / paid date mapping under the deductible basis (#7380). */
export const CHECK_PAID_DATE = "paid_date_mapping";
export const advisoryCheckKey = (kind: string) => `advisory:${kind}`;

export interface ErroredReasonGroupInput {
  rule: string;
  label: string;
  /** Record indices in this group the reviewer has NOT excluded. */
  remaining: number[];
  /** Schemas carrying records in this group, most records first. */
  schemas: TargetSchema[];
  onShow: (schema: TargetSchema, indices: number[]) => void;
  onExclude: () => void;
}

export interface ReviewChecksInput {
  /** False for a viewer: no acknowledge, exclude or fix controls. */
  canFix: boolean;
  acknowledged: ReadonlyMap<string, string>;
  onUndo: (check: string) => void;

  mapping: {
    blockingIssues: Array<{ schema: TargetSchema; message: string }>;
    requiredGaps: CriticalFieldAdvisoryEntry[];
    /** Record-level transformation warnings in the loaded records. */
    transformationWarningCount: number;
    onOpenFieldMapping?: () => void;
  };

  rows: {
    /** What the approve gate counts: whole-document unresolved errored records (exclusion-aware). */
    unresolvedCount: number;
    unresolvedCountIsExact: boolean;
    /** Errors in the loaded records — the fallback when the whole-document read is unavailable. */
    loadedErrorCount: number;
    groups: ErroredReasonGroupInput[];
    notChecked: boolean;
    isLoading: boolean;
    readFailed: boolean;
    schemaLabel: (schema: TargetSchema) => string;
  };

  /** Null when the document has no claim lines to reconcile (reimbursement docs). */
  ytd: (YtdReconciliation & { onShow?: () => void }) | null;

  asOf: {
    show: boolean;
    /** Formatted date ("Sep 30, 2026") or null when unset. */
    label: string | null;
    isInFuture: boolean;
    /** The chosen date precedes the latest paid claim in the document (#7439 AC2). */
    isStale: boolean;
    isMissingForYtd: boolean;
    latestClaimPaidLabel: string | null;
    /** True when the latest-paid comparison covered the loaded page only (paged document, no server answer). */
    latestPaidCheckPartial?: boolean;
    /**
     * Where the current value came from (#7439 AC1/AC4). "defaulted" is the
     * prior-month-end guess the server made because the file states no date:
     * it needs the reviewer's confirmation before the count clears. Once the
     * reviewer changes the date it is "reviewer".
     */
    source: "reviewer" | "file" | "defaulted" | "none";
    onChange?: () => void;
  };

  advisories: ProjectionWarning[];
  onCompareSource?: () => void;
  /**
   * Claim lines outside the policy dates (#7436): the groups behind the
   * `items_outside_policy_period` advisory, exclusion-aware, with the windows
   * line for the card body. Null when the surface has no claim lines.
   */
  outsidePolicy?: {
    groups: Array<{
      key: string;
      label: string;
      count: number;
      amount: number | null;
      /** Loaded rows in the group (what Show in grid / Exclude act on); may be fewer than `count` on a paged document. */
      loadedCount?: number;
      onShow?: () => void;
      onExclude?: () => void;
    }>;
    windowsLabel: string | null;
    /**
     * Which accumulation engine judged the lines (`policy_windows.engine`).
     * Under `legacy` the stamp is is_eligible=false but the load still sums the
     * lines, and so does the forecast beside the card — the body must not say
     * they won't count.
     */
    engine?: "deductible_basis" | "legacy" | null;
  } | null;

  /**
   * Paid amount / paid date mapping verdict (#7380, see paidDateCoverage.ts).
   * Null or undefined when the document has no claim lines to judge.
   */
  paidDate?: PaidDateCoverage | null;
  /**
   * Route to the claim-lines list filtered to Missing paid date, scoped to the
   * document's client (#7380). Null when the reviewer may not list claim lines
   * or the deductible-basis flag is off (the legacy stamp never writes that
   * reason, so the list would be empty); the card then keeps its in-page actions
   * and does not name the facet.
   */
  claimLinesWithoutPaidDateHref?: string | null;

  completeness?: CompletenessCheck;
  doubleCountWarningMessage?: string;

  exclusions: {
    claimants: number;
    lines: number;
    onRestoreAll?: () => void;
  };

  counts: {
    loadedClaimants: number;
    totalClaimants: number | null;
    loadedLines: number;
    partial: boolean;
  };

  /** Template / saved-layout name; null → "Automatic extraction". */
  layoutLabel: string | null;
  /**
   * What the extractor did to the rows and did not report (#6921): subtotal
   * rows skipped, blank identity rows filled from the row above. Undefined
   * when the transformation predates the measurement.
   */
  rowDecisions?: TransformationRowDecisionsPayload;
  isProjectionChecking: boolean;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const int = (n: number) => n.toLocaleString();

/** Acknowledge button label per projection-advisory kind; the generic fallback is honest rather than specific. */
const advisoryAckLabel = (kind: string): string => {
  switch (kind) {
    case "items_outside_policy_period":
      return "Dates are right";
    case "layout_determination_ambiguous":
      return "Layout is right";
    case "possible_missing_family_deductible":
      return "Load as individuals";
    case "reimbursement_template_missing_fields":
    case "eor_filter_dropped_all_records":
      return "Load it anyway";
    default:
      return "Noted";
  }
};

/** Pass-line text once an advisory is acknowledged: what was confirmed, in the design's words. */
const advisoryPassText = (warning: ProjectionWarning, title: string): string => {
  const d = warning.details;
  const out = d && typeof d.items_out_of_range === "number" ? d.items_out_of_range : null;
  switch (warning.kind) {
    case "layout_determination_ambiguous":
      return "Layout confirmed by you";
    case "items_outside_policy_period":
      return out !== null
        ? `${int(out)} ${plural(out, "line", "lines")} outside policy dates confirmed by you`
        : "Lines outside policy dates confirmed by you";
    case "possible_missing_family_deductible":
      return "Claimants load as individuals, confirmed by you";
    default:
      return `${title} — confirmed by you`;
  }
};

export const buildReviewChecks = (input: ReviewChecksInput): ReviewChecks => {
  const cards: ReviewCheckCard[] = [];
  const passLines: ReviewPassLine[] = [];
  const placedAcks: PlacedAcknowledgement[] = [];
  const { canFix, acknowledged, onUndo } = input;

  // A card the reviewer acknowledged becomes a pass line with Undo and stops
  // counting. Blocking cards never reach here (no `acknowledge`).
  //
  // Acknowledged means the stored DETAIL matches the card's current detail,
  // not just the key (!4936 review, [AEM-ack-stale]). The detail is the line
  // the reviewer saw — "3 claimants' reported YTD don't match", "Approved
  // with no paid-through date" — so when the state behind it changes (more
  // pages load and 9 mismatch; a date gets set; the rows are excluded) the
  // card comes back and the old acknowledgement is not sent. #7432 AC4 and
  // #7440 AC6/AC7 rely on the acknowledgement describing what was actually
  // on screen.
  const place = (card: ReviewCheckCard) => {
    if (card.acknowledge && acknowledged.get(card.key) === card.acknowledge.detail) {
      placedAcks.push({ check: card.key, detail: card.acknowledge.detail });
      passLines.push({
        key: card.key,
        text: card.acknowledge.passText,
        state: "pass",
        action: canFix
          ? { label: "Undo", ariaLabel: card.acknowledge.undoLabel, onClick: () => onUndo(card.key) }
          : undefined,
      });
      return;
    }
    cards.push({ ...card, acknowledge: canFix ? card.acknowledge : undefined });
  };

  // ── Paid-through date (#7439, artboard 3b; ADR-051 §3) ──────────────────
  // Exactly three cases need the reviewer's attention, in this order: a future
  // date (blocking — the carrier's totals would win over every later claim
  // line), a date before the latest paid claim (advisory), and a date the
  // server defaulted because the file states none (advisory). A missing date
  // on a YTD file stays advisory (#7392). Everything else is a pass line.
  if (input.asOf.show) {
    const a = input.asOf;
    const change = a.onChange ? [{ label: "Change date", onClick: a.onChange, variant: "outlined" as const }] : [];
    const scope = a.latestPaidCheckPartial ? " Checked against the loaded records only." : "";
    if (a.isInFuture) {
      place({
        key: CHECK_PAID_THROUGH,
        severity: "blocking",
        title: "Paid-through date is in the future",
        body: "Correct it to approve.",
        hint: `${a.label ?? "The date"} is after today. A future paid-through date would make the carrier's reported totals win over every later claim line.`,
        actions: change,
        shortName: "Paid-through date",
      });
    } else if (a.isStale && a.label) {
      const latest = a.latestClaimPaidLabel ?? "a later date";
      place({
        key: CHECK_PAID_THROUGH,
        severity: "advisory",
        title: "Paid-through date is before the latest paid claim",
        body: `The file has claims paid through ${latest}.${scope}`,
        hint: "A paid-through date earlier than the latest paid claim usually means the carrier's reporting period and the claim lines disagree. Confirm it, or change the date.",
        actions: change,
        acknowledge: {
          label: "Date is right",
          detail: `Paid through ${a.label}, before the latest paid claim ${latest}`,
          passText: `Paid through ${a.label} confirmed by you`,
          undoLabel: "Undo confirming the paid-through date",
        },
        shortName: "Paid-through date",
      });
    } else if (a.source === "defaulted" && a.label) {
      place({
        key: CHECK_PAID_THROUGH,
        severity: "advisory",
        title: "Confirm the paid-through date",
        body: `The file doesn't state one. We used ${a.label}, the month-end before upload.`,
        hint: "Carrier YTD reports lag, so a file uploaded mid-month usually reports through the previous month-end. Nothing in the file supports this date — confirm it or change it.",
        actions: change,
        acknowledge: {
          label: `${a.label} is right`,
          detail: `Paid through ${a.label}, defaulted to the month-end before upload`,
          passText: `Paid through ${a.label} confirmed by you`,
          undoLabel: "Undo confirming the paid-through date",
        },
        shortName: "Paid-through date",
      });
    } else if (a.isMissingForYtd) {
      place({
        key: CHECK_PAID_THROUGH,
        severity: "advisory",
        title: "No paid-through date",
        body: "This file maps a carrier YTD total but no paid-through date is set. On approve the carrier total still becomes the official figure, and without a date newer claim lines can never take over. Set it unless that is intended.",
        actions: change,
        acknowledge: {
          label: "No date is right",
          detail: "Approved with no paid-through date",
          passText: "Paid-through date left unset by you",
          undoLabel: "Undo leaving the paid-through date unset",
        },
        shortName: "Paid-through date",
      });
    } else if (a.label) {
      // "confirmed by you" is the acknowledged card's pass text (place() above);
      // this line is for a date nothing questioned.
      passLines.push({
        key: CHECK_PAID_THROUGH,
        text: `Paid through ${a.label} — ${a.source === "reviewer" ? "set by you" : "from the file header"}`,
        state: "pass",
        action: a.onChange
          ? { label: "Change", ariaLabel: "Change paid-through date", onClick: a.onChange }
          : undefined,
      });
    }
  }

  // ── Money and date columns (field mapping) ──────────────────────────────
  {
    const m = input.mapping;
    const openMapping = m.onOpenFieldMapping
      ? [{ label: "Open field mapping", onClick: m.onOpenFieldMapping, variant: "outlined" as const }]
      : [];
    if (m.blockingIssues.length > 0) {
      const n = m.blockingIssues.length;
      place({
        key: CHECK_MONEY_DATE_COLUMNS,
        severity: "blocking",
        title: `${int(n)} field-mapping ${plural(n, "issue blocks", "issues block")} approval`,
        body: m.blockingIssues
          .slice(0, 3)
          .map((i) => i.message)
          .join(" "),
        actions: openMapping,
        shortName: "Money and date columns",
      });
    } else if (m.requiredGaps.length > 0 || m.transformationWarningCount > 0) {
      const gaps = m.requiredGaps.map((g) => formatToTitleCase(g.entry.field));
      const parts: string[] = [];
      if (gaps.length > 0) {
        parts.push(
          `${gaps.slice(0, 4).join(", ")}${gaps.length > 4 ? ` and ${gaps.length - 4} more` : ""} ${plural(gaps.length, "isn't", "aren't")} mapped — records will error on approve until ${plural(gaps.length, "it is", "they are")}.`
        );
      }
      if (m.transformationWarningCount > 0) {
        parts.push(
          `${int(m.transformationWarningCount)} ${plural(m.transformationWarningCount, "value", "values")} could not be transformed cleanly — a wrong money or date column looks like this.`
        );
      }
      place({
        key: CHECK_MONEY_DATE_COLUMNS,
        severity: "advisory",
        title: "Check the money and date columns",
        body: parts.join(" "),
        actions: openMapping,
        acknowledge: {
          label: "Columns are right",
          detail: parts.join(" "),
          passText: "Money and date columns confirmed by you",
          undoLabel: "Undo column confirmation",
        },
        shortName: "Money and date columns",
      });
    } else {
      passLines.push({
        key: CHECK_MONEY_DATE_COLUMNS,
        text: "Money and date columns mapped without issues",
        state: "pass",
      });
    }
  }

  // ── Paid date / paid amount mapping (#7380, ADR-081 D5.1) ───────────────
  // Advisory, never blocking: the lines still load, each explains itself and
  // has an editable Paid Date, and the reason filter finds them afterwards. But
  // the mapping step is the earliest point a person can act, so the card links
  // there. Acknowledging says "load them anyway"; the count travels with it.
  // The projection's outside-policy advisory (#7436) also groups these lines as
  // "No paid date". One card owns them: this one, because it carries the fix
  // (Open field mapping). That group is dropped from the outside-policy card
  // below, and its exclusion-aware count and Show in grid are reused here.
  const projectedNoPaidDate =
    input.paidDate?.kind === "missing_paid_date"
      ? (input.outsidePolicy?.groups.find((g) => g.key === "missing_paid_date") ?? null)
      : null;
  if (input.paidDate) {
    const pd = input.paidDate;
    const m = input.mapping;
    const openMapping = m.onOpenFieldMapping
      ? [{ label: "Open field mapping", onClick: m.onOpenFieldMapping, variant: "outlined" as const }]
      : [];
    if (pd.kind === "missing_paid_date" && projectedNoPaidDate && projectedNoPaidDate.count === 0) {
      // Every such line has been excluded by the reviewer: nothing left to warn about.
      passLines.push({ key: CHECK_PAID_DATE, text: "Lines without a paid date excluded by you", state: "pass" });
    } else if (pd.kind === "missing_paid_date") {
      // Exact only when the projection judged the rows. The coverage fallback
      // (flag off, or no resolvable policy windows) counts every line without a
      // paid date, including ones that load Submitted (non-paid status, blank
      // amount) and are not counted by the load record: an upper bound, said so.
      const n = projectedNoPaidDate?.count ?? pd.count;
      const exact = projectedNoPaidDate !== null;
      const title =
        n !== null
          ? `${exact ? "" : "Up to "}${int(n)} claim ${plural(n, "line has", "lines have")} no paid date`
          : "Claim lines have no paid date";
      const money =
        projectedNoPaidDate?.amount !== null && projectedNoPaidDate?.amount !== undefined
          ? `${formatUSD(projectedNoPaidDate.amount)} won't count toward the deductible until a paid date is mapped.`
          : "They will not count toward the deductible until a paid date is mapped.";
      const show: ReviewCheckAction[] = projectedNoPaidDate?.onShow
        ? [
            {
              label: "Show in grid",
              onClick: projectedNoPaidDate.onShow,
              variant: "text",
              ariaLabel: "Show lines without a paid date in grid",
            },
          ]
        : [];
      // Pre-load, the list holds this client's EXISTING backlog, not this file's
      // lines; the link is where the by-hand fix happens once the load is in.
      const openList: ReviewCheckAction[] = input.claimLinesWithoutPaidDateHref
        ? [
            {
              label: "Open Claim Items",
              onClick: () => {},
              href: input.claimLinesWithoutPaidDateHref,
              variant: "text",
              ariaLabel: "Open Claim Items filtered to lines missing a paid date",
            },
          ]
        : [];
      place({
        key: CHECK_PAID_DATE,
        severity: "advisory",
        title,
        body: `${money} Map the file's paid-date column, or load now and set each line's Paid Date by hand${
          openList.length > 0 ? " in Claim Items, filtered to Missing paid date" : " on each line"
        }. A line reloaded over one that already has a paid date keeps it.`,
        hint: "Under the deductible basis a line counts on the day it was paid. With no paid date it cannot be placed in a paid window, so it never counts. No stand-in date is acceptable (#7245).",
        actions: [...openMapping, ...show, ...openList],
        acknowledge: {
          label: "Load without paid dates",
          detail: title,
          passText: `${title} — loaded without paid dates by you`,
          undoLabel: "Undo loading without paid dates",
        },
        shortName: "Paid date",
      });
    } else if (pd.kind === "missing_paid_amount") {
      const title = "No paid amount is mapped";
      place({
        key: CHECK_PAID_DATE,
        severity: "advisory",
        title,
        body: "Every line will load as Submitted and count nowhere toward the deductible. If the file's amount column is what the carrier paid, map it as Paid Amount.",
        hint: "Mapping a paid-amount column is what marks a loaded line Paid; only paid lines accumulate under the deductible basis. A billed or allowed amount is not a paid amount.",
        actions: openMapping,
        acknowledge: {
          label: "No paid amount is right",
          detail: title,
          passText: "No paid amount mapped — confirmed by you",
          undoLabel: "Undo confirming no paid amount",
        },
        shortName: "Paid amount",
      });
    } else {
      passLines.push({ key: CHECK_PAID_DATE, text: "Paid amount and paid date mapped", state: "pass" });
    }
  }

  // ── Reported YTD vs claim lines ─────────────────────────────────────────
  if (input.ytd && input.ytd.available) {
    const y = input.ytd;
    const n = y.mismatches.length;
    if (n > 0) {
      const title = `${int(n)} ${plural(n, "claimant's", "claimants'")} reported YTD ${plural(n, "doesn't", "don't")} match the claim lines`;
      place({
        key: CHECK_YTD_MISMATCH,
        severity: "advisory",
        title,
        body: y.largest ? `Largest gap ${formatUSD(Math.abs(y.largest.diff))} · ${y.largest.name}` : undefined,
        hint: "A gap usually means claim lines are missing from the file, duplicated, or the wrong paid column is mapped.",
        actions: y.onShow ? [{ label: `Show the ${int(n)}`, onClick: y.onShow, variant: "outlined" }] : [],
        acknowledge: {
          label: "Differences are expected",
          detail: title,
          passText: `${int(n)} YTD ${plural(n, "difference", "differences")} marked expected by you`,
          undoLabel: "Undo marking YTD differences expected",
        },
        shortName: `${int(n)} YTD ${plural(n, "difference", "differences")}`,
      });
    } else {
      passLines.push({
        key: CHECK_YTD_MISMATCH,
        text: `Reported YTD matches the claim lines for all ${int(y.claimantsChecked)}${input.counts.partial ? " loaded" : ""}`,
        state: "pass",
      });
    }
  }

  // ── Claim lines that can't load ─────────────────────────────────────────
  {
    const r = input.rows;
    const n = Math.max(r.unresolvedCount, r.loadedErrorCount);
    if (r.notChecked) {
      passLines.push({
        key: CHECK_ROWS_CANNOT_LOAD,
        text: "Not checked for rows that can't load — this file was processed before the check existed. Reprocess it to check.",
        state: "note",
      });
    } else if (r.isLoading && n === 0) {
      passLines.push({ key: CHECK_ROWS_CANNOT_LOAD, text: "Checking rows that can't load…", state: "pending" });
    } else if (n > 0) {
      const rows: ReviewCheckRow[] = r.groups
        .filter((g) => g.remaining.length > 0)
        .map((g) => ({
          key: g.rule,
          label: g.label,
          count: g.remaining.length,
          onShow: g.schemas[0] ? () => g.onShow(g.schemas[0], g.remaining) : undefined,
          onExclude: canFix ? g.onExclude : undefined,
        }));
      const count = r.unresolvedCountIsExact ? int(n) : `At least ${int(n)}`;
      // The card is title + one row per reason. The advice lives behind the
      // info icon, and "Open field mapping" is not repeated here — Fix the
      // extraction at the foot of the rail is that control (walkthrough
      // feedback 2026-10-15: three jobs in one card read as noise).
      // One reason (the common case): the title already carries the count, so
      // the reason is the card's one line and its Show / Exclude are the
      // card's actions. Several reasons: one plain row each, counts differ.
      const single = rows.length === 1 ? rows[0] : null;
      const singleActions: ReviewCheckAction[] = single
        ? [
            ...(single.onShow
              ? [
                  {
                    label: "Show in grid",
                    onClick: single.onShow,
                    variant: "text" as const,
                    ariaLabel: `Show ${single.label} rows in grid`,
                  },
                ]
              : []),
            ...(single.onExclude
              ? [
                  {
                    label: `Exclude ${int(single.count)}`,
                    onClick: single.onExclude,
                    variant: "outlined" as const,
                    ariaLabel: `Exclude ${int(single.count)} ${single.label} rows`,
                  },
                ]
              : []),
          ]
        : [];
      place({
        key: CHECK_ROWS_CANNOT_LOAD,
        severity: "blocking",
        title: `${count} claim ${plural(n, "line", "lines")} can't load`,
        body: single
          ? single.label
          : rows.length === 0
            ? r.readFailed
              ? "Could not break these down by reason. Retry, or fix the mapping and exclude rows from the grid."
              : "Fix the mapping if a column is mis-mapped, or exclude the rows from the grid."
            : undefined,
        hint: "These rows fail validation and will not load. If a column is mis-mapped, fix the mapping (Fix the extraction, below); if the rows are junk, exclude them. Loading more records will not help.",
        rows: single ? undefined : rows,
        actions: singleActions,
        shortName: `${int(n)} claim ${plural(n, "line", "lines")} can't load`,
      });
    } else if (r.readFailed) {
      passLines.push({
        key: CHECK_ROWS_CANNOT_LOAD,
        text: "Could not check the whole file for rows that can't load — retry before approving.",
        state: "note",
      });
    } else {
      passLines.push({ key: CHECK_ROWS_CANNOT_LOAD, text: "Every claim line can load", state: "pass" });
    }
  }

  // ── Projection advisories ───────────────────────────────────────────────
  for (const warning of input.advisories) {
    let copy = describeAdvisory(warning);
    const actions: ReviewCheckAction[] = [];
    let rows: ReviewCheckRow[] | undefined;
    let ackLabelOverride: string | null = null;
    if (copy.action?.kind === "compare-source" && input.onCompareSource) {
      actions.push({ label: copy.action.label, onClick: input.onCompareSource, variant: "outlined" });
    }
    if (copy.action?.kind === "field-mapping" && input.mapping.onOpenFieldMapping) {
      actions.push({ label: copy.action.label, onClick: input.mapping.onOpenFieldMapping, variant: "outlined" });
    }
    // Outside-policy lines (#7436 AC1): the card carries the policy's windows,
    // one row per reason with count and $, Show in grid / Exclude N per row,
    // and the total that won't count. Counts fall as lines are excluded; once
    // every line is excluded there is nothing left to confirm and no card.
    if (warning.kind === "items_outside_policy_period" && input.outsidePolicy) {
      // The paid-date card (#7380) owns the "No paid date" lines when it is showing.
      const groups = input.outsidePolicy.groups.filter(
        (g) => g.count > 0 && !(projectedNoPaidDate && g.key === "missing_paid_date")
      );
      // No groups left means every flagged line is excluded: nothing to
      // confirm, no card. But only when the server judged the lines at all
      // (`details.groups`). An API without the per-line verdicts (SPA deployed
      // ahead of coronis-apps !4967) sends this advisory with no groups, and
      // then describeAdvisory's whole-set copy must still show, or the
      // overstated "$X over deductible" goes unexplained.
      const serverJudgedLines = Array.isArray(warning.details?.groups);
      if (groups.length === 0) {
        if (serverJudgedLines) continue;
      } else {
        const n = groups.reduce((sum, g) => sum + g.count, 0);
        const total = groups.every((g) => g.amount !== null)
          ? groups.reduce((sum, g) => sum + (g.amount ?? 0), 0)
          : null;
        rows = groups.map((g) => ({
          key: g.key,
          label: g.label,
          count: g.count,
          detail: g.amount !== null ? formatUSD(g.amount) : undefined,
          onShow: g.onShow,
          onExclude: canFix ? g.onExclude : undefined,
          // Paged document: the server counted the whole projection, the page
          // holds fewer; Exclude acts on the loaded ones and says so.
          excludeCount: g.loadedCount !== undefined && g.loadedCount < g.count ? g.loadedCount : undefined,
        }));
        // Window groups read "outside the policy dates"; the basis engine's
        // missing-field groups (no paid date, no service date, no amount) are
        // not a dates question, so the title and the confirm button generalise.
        const windowsOnly = groups.every(
          (g) => g.key === "paid_date_outside_policy" || g.key === "service_date_outside_incurred_period"
        );
        if (!windowsOnly) ackLabelOverride = "Lines are right";
        copy = {
          title: windowsOnly
            ? `${int(n)} claim ${plural(n, "line is", "lines are")} outside the policy dates`
            : `${int(n)} claim ${plural(n, "line won't", "lines won't")} count toward the deductible`,
          body: [
            input.outsidePolicy.windowsLabel,
            input.outsidePolicy.engine === "legacy"
              ? "These lines load stamped ineligible. This environment's legacy accumulation still counts them in the YTD and breach shown here."
              : total !== null
                ? `${formatUSD(total)} won't count toward deductibles. The lines still load.`
                : "These lines load but won't count toward deductibles.",
          ]
            .filter(Boolean)
            .join(" · "),
        };
      }
    }
    const ackLabel = ackLabelOverride ?? advisoryAckLabel(warning.kind);
    place({
      key: advisoryCheckKey(warning.kind),
      severity: "advisory",
      title: copy.title,
      body: copy.body,
      rows,
      actions,
      acknowledge: {
        label: ackLabel,
        detail: copy.title,
        passText: advisoryPassText(warning, copy.title),
        undoLabel: `Undo "${ackLabel}"`,
      },
      shortName: copy.title,
    });
  }

  // ── Completeness ────────────────────────────────────────────────────────
  if (input.completeness && input.completeness.status !== "passed") {
    const c = input.completeness;
    const missing = c.expected_members - c.loaded_claimants;
    const title =
      c.status === "unverifiable"
        ? "Claimant count couldn't be verified"
        : missing > 0
          ? `${int(missing)} ${plural(missing, "member", "members")} may have been dropped`
          : missing < 0
            ? `${int(-missing)} extra ${plural(-missing, "claimant", "claimants")} loaded`
            : "Member counts don't match";
    const body =
      c.status === "unverifiable"
        ? `The file's member sections couldn't be counted against the ${int(c.loaded_claimants)} ${plural(c.loaded_claimants, "claimant", "claimants")} loaded. Confirm against the source before approving.`
        : `The file lists ${int(c.expected_members)} ${plural(c.expected_members, "member", "members")}; ${int(c.loaded_claimants)} ${plural(c.loaded_claimants, "claimant", "claimants")} loaded. ${c.detail ? `${c.detail} ` : ""}Verify against the source before approving.`;
    place({
      key: CHECK_COMPLETENESS,
      severity: "advisory",
      title,
      body,
      actions: input.onCompareSource
        ? [{ label: "Compare with source", onClick: input.onCompareSource, variant: "outlined" }]
        : [],
      acknowledge: {
        label: "Count is right",
        detail: title,
        passText: "Claimant count confirmed by you",
        undoLabel: "Undo confirming the claimant count",
      },
      shortName: "Claimant count",
    });
  }

  // ── Rx double count (ADR-077 §8) ────────────────────────────────────────
  if (input.doubleCountWarningMessage) {
    place({
      key: CHECK_RX_DOUBLE_COUNT,
      severity: "advisory",
      title: "Reported total may already include Rx",
      body: input.doubleCountWarningMessage,
      actions: [],
      acknowledge: {
        label: "Not double-counting",
        detail: "Reported total may already include Rx that also loads as carve-out lines",
        passText: "Rx double-count confirmed not to apply by you",
        undoLabel: "Undo confirming the Rx double-count",
      },
      shortName: "Rx double count",
    });
  }

  // ── Row decisions (#6921) — disclosure, not a verdict ───────────────────
  // Both heuristics are usually right, so these are information lines with
  // the "why" behind an icon, never cards. Moved here from above the grid
  // tabs (walkthrough feedback 2026-10-15): they are about what will load,
  // which is what the rail is for.
  if (input.rowDecisions) {
    const { skipped_aggregate_rows: skipped, forward_filled_rows: filled } = input.rowDecisions;
    if (skipped > 0) {
      passLines.push({
        key: "rows-skipped-subtotals",
        text: `${int(skipped)} ${plural(skipped, "row", "rows")} skipped as subtotals — not loaded`,
        state: "note",
        hint: "Rows with amounts filled and the detail columns blank are treated as subtotal lines and not loaded, because loading them double-counts. Check the source if a row count looks wrong.",
      });
    }
    if (filled > 0) {
      passLines.push({
        key: "rows-forward-filled",
        text: `${int(filled)} ${plural(filled, "row", "rows")} took a claimant from the row above`,
        state: "note",
        hint: "Rows with blank identity columns take the claimant from the row above, as vendors lay out a claimant header followed by its claim lines. Check the source if a claimant name looks wrong.",
      });
    }
  }

  // ── Layout ──────────────────────────────────────────────────────────────
  // A pass line only when no layout card is showing — the card covers it.
  const layoutCardShowing = input.advisories.some((w) => w.kind === "layout_determination_ambiguous");
  if (!layoutCardShowing) {
    passLines.push({
      key: "layout",
      text: input.layoutLabel ? `Saved layout used — ${input.layoutLabel}` : "File layout recognised",
      state: "pass",
    });
  }

  // ── Exclusions ──────────────────────────────────────────────────────────
  {
    const e = input.exclusions;
    const restore =
      canFix && e.onRestoreAll
        ? { label: "Include again", ariaLabel: "Include excluded rows again", onClick: e.onRestoreAll }
        : undefined;
    if (e.claimants > 0) {
      passLines.push({
        key: "excluded-claimants",
        text: `${int(e.claimants)} ${plural(e.claimants, "claimant", "claimants")} excluded by you${e.lines > 0 ? ` (${int(e.lines)} claim ${plural(e.lines, "line", "lines")})` : ""}`,
        state: "pass",
        action: restore,
      });
    } else if (e.lines > 0) {
      passLines.push({
        key: "excluded-lines",
        text: `${int(e.lines)} claim ${plural(e.lines, "line", "lines")} excluded by you`,
        state: "pass",
        action: restore,
      });
    }
  }

  // Order: blocking first, then advisory — the reviewer works top to bottom.
  cards.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "blocking" ? -1 : 1));

  const checksTotal = 4; // mapping · rows · projection (YTD + advisories) · completeness
  const pending = (input.rows.isLoading ? 1 : 0) + (input.isProjectionChecking ? 1 : 0);
  if (input.isProjectionChecking) {
    passLines.push({ key: "projection-pending", text: "Comparing reported YTD with claim lines…", state: "pending" });
  }

  return {
    cards,
    passLines,
    acknowledgements: placedAcks,
    attentionCount: cards.length,
    attentionNames: cards.map((c) => c.shortName),
    isChecking: pending > 0,
    checksDone: checksTotal - pending,
    checksTotal,
  };
};
